import { cn } from '@neo/lib/utils'
import { Banknote, CreditCard, Landmark } from 'lucide-react'
import type { RCard } from 'v2/types/api/billing'
import { ZERO_FEE_LABEL } from '../recharge-shared'
import { CardSelector } from './card-selector'
import { MethodTile } from './method-tile'

// The three payment methods the Razorpay recharge flow can offer. Every entry
// point renders the SAME picker from here: which methods exist, what their copy
// says and when they are disabled is gateway policy, not per-modal decoration,
// so it lives in one component rather than being spelled per call site (it was
// spelled twice once, and the copies drifted — one had no picker at all, which
// is how netbanking and bank transfer disappeared from the Add Credit flow).
export type RazorpayMethod = 'card' | 'netbanking' | 'bank_transfer'

// Hoisted so the `activeCards` default is one stable reference rather than a
// fresh [] on every render — a new array identity propagates straight into
// CardSelector's props and defeats any memo comparison downstream.
const NO_CARDS: RCard[] = []

interface RazorpayMethodPickerProps {
  method: RazorpayMethod
  onSelect: (method: RazorpayMethod) => void
  showNetbanking: boolean
  showBankTransfer: boolean
  cardFeeLabel?: string
  // Set when the selected amount is below that rail's INR floor — the tile then
  // renders disabled with this as its subtitle, so the customer sees WHY rather
  // than the method silently vanishing. One per rail: both floors are ₹10,000
  // today, for independent reasons, so they are passed separately.
  netbankingDisabledReason?: string
  bankTransferDisabledReason?: string
  disabled?: boolean
  // Saved cards belong to the Card method, so the list hangs inside that tile's
  // outline rather than sitting below the other modes as a fourth, unrelated
  // control. Omitting `activeCards` (or passing none) falls back to the generic
  // Card row plus an add-card affordance.
  activeCards?: RCard[]
  selectedCard?: number
  onCardChange?: (cardId: number) => void
  onAddCard?: () => void
}

export const RazorpayMethodPicker = ({
  method,
  onSelect,
  showNetbanking,
  showBankTransfer,
  cardFeeLabel,
  netbankingDisabledReason,
  bankTransferDisabledReason,
  disabled,
  activeCards = NO_CARDS,
  selectedCard = 0,
  onCardChange,
  onAddCard,
}: RazorpayMethodPickerProps) => {
  // The card list is only wired in where the host actually manages a selection.
  // The add-card entry point pins the method and vaults a new card, so it passes
  // no handlers and keeps the plain Card tile.
  const showCardList = !!onCardChange && !!onAddCard
  // With a card saved, that card IS the row: a generic "Card / Pay using credit
  // or debit card" header above a Visa ending 4242 says nothing the card doesn't
  // already say, so the placeholder gives way and the mode's fee moves onto the
  // card row. The generic row is what you get only before the first card exists.
  const hasSavedCard = activeCards.length > 0

  return (
    <div>
      <h4 className="mb-3 text-p-600">Select Payment Mode</h4>
      <div className="flex flex-col gap-2">
        {showCardList ? (
          <div
            className={cn(
              'overflow-hidden rounded border',
              method === 'card' ? 'border-primary' : 'border-border hover:border-hv1',
            )}
          >
            {hasSavedCard ? (
              <CardSelector
                activeCards={activeCards}
                selectedCard={selectedCard}
                onCardChange={onCardChange}
                onAddCard={onAddCard}
                onTriggerClick={() => onSelect('card')}
                badge={cardFeeLabel}
                isPending={disabled}
                // Only the chosen mode expands to offer "Add a new card";
                // collapsed, every mode is a single row.
                showAddCardButton={method === 'card'}
                embedded
              />
            ) : (
              // No card on file yet: the generic row stands in, and nothing hangs
              // under it. The dialog's primary button is what collects the first
              // card, so an "Add card" row here would be a second control for the
              // same job.
              <MethodTile
                method="card"
                selected={method}
                onSelect={onSelect}
                icon={<CreditCard className="size-5" />}
                title="Credit/Debit Card"
                // The fee IS the subtext on every mode row — what it costs to use
                // this rail is the thing worth saying under its name, and it
                // leaves the right edge free for the card's Default pill.
                subtitle={cardFeeLabel ?? 'Pay using credit or debit card'}
                disabled={disabled}
                bordered={false}
              />
            )}
          </div>
        ) : (
          <MethodTile
            method="card"
            selected={method}
            onSelect={onSelect}
            icon={<CreditCard className="size-5" />}
            title="Credit/Debit Card"
            subtitle={cardFeeLabel ?? 'Pay using credit or debit card'}
            disabled={disabled}
          />
        )}
        {showNetbanking && (
          <MethodTile
            method="netbanking"
            selected={method}
            onSelect={onSelect}
            icon={<Landmark className="size-5" />}
            title="Netbanking"
            // Below the floor the reason takes the line: what it costs matters
            // less than the fact you can't use it at this amount.
            subtitle={netbankingDisabledReason ?? ZERO_FEE_LABEL}
            disabled={disabled || netbankingDisabledReason !== undefined}
          />
        )}
        {showBankTransfer && (
          <MethodTile
            method="bank_transfer"
            selected={method}
            onSelect={onSelect}
            icon={<Banknote className="size-5" />}
            title="Bank Transfer"
            subtitle={bankTransferDisabledReason ?? ZERO_FEE_LABEL}
            disabled={disabled || bankTransferDisabledReason !== undefined}
          />
        )}
      </div>
    </div>
  )
}
